import helpers from "@/helpers/index"
import { attachPbisPointsSocketListeners } from "@/helpers/behaviourPbisSocket.js"
import store from "@/store/index"
import localforage from "localforage"
import tokenService from "@/helpers/tokenService.js"
import Echo from "laravel-echo"
import download from "@/helpers/downloadCSV.js"
import { useToast } from "vue-toastification"
import queueUtils from "@/helpers/queueUtils"

let socket = null
let wsReconnectInterval = null
let isManualDisconnect = false
let hadNetworkDisconnect = false
let hasConnectedOnce = false
let socketConnectionCleanup = null
let dashboardRefetchDebounceTimer = null
let dashboardRefetchCooldownTimer = null
let refetchInFlight = false
let lastDashboardRefetchAt = 0
let isSocketConnected = false

// Wait for the socket to settle after a flap before hitting the API.
const DASHBOARD_REFETCH_DEBOUNCE_MS = 2000
// Cap network-recovery refetches so flaky Wi‑Fi cannot spam dashboard endpoints.
const DASHBOARD_REFETCH_COOLDOWN_MS = 60 * 1000

const UNHEALTHY_CONNECTION_STATES = ["disconnected", "unavailable", "failed"]

const markUnexpectedNetworkDisconnect = () => {
  if (!isManualDisconnect) {
    hadNetworkDisconnect = true
  }
}

const executeDashboardRefetchAfterReconnect = async () => {
  if (!hadNetworkDisconnect) {
    return
  }
  if (refetchInFlight) {
    return
  }
  if (!isSocketConnected) {
    return
  }
  if (typeof document !== "undefined" && document.hidden) {
    return
  }

  const now = Date.now()
  const timeSinceLastRefetch = now - lastDashboardRefetchAt

  if (timeSinceLastRefetch < DASHBOARD_REFETCH_COOLDOWN_MS) {
    const retryInMs = DASHBOARD_REFETCH_COOLDOWN_MS - timeSinceLastRefetch
    clearTimeout(dashboardRefetchCooldownTimer)
    dashboardRefetchCooldownTimer = setTimeout(() => {
      executeDashboardRefetchAfterReconnect()
    }, retryInMs)
    return
  }

  hadNetworkDisconnect = false
  refetchInFlight = true
  lastDashboardRefetchAt = now
  try {
    await store.dispatch("dashboardTable/refetchAfterSocketReconnect")
  } finally {
    refetchInFlight = false
    if (hadNetworkDisconnect && isSocketConnected) {
      scheduleDashboardRefetchAfterReconnect()
    }
  }
}

const scheduleDashboardRefetchAfterReconnect = () => {
  if (!hadNetworkDisconnect || !hasConnectedOnce) {
    return
  }

  clearTimeout(dashboardRefetchDebounceTimer)
  dashboardRefetchDebounceTimer = setTimeout(() => {
    executeDashboardRefetchAfterReconnect()
  }, DASHBOARD_REFETCH_DEBOUNCE_MS)
}

const handleSocketConnected = () => {
  isSocketConnected = true
  scheduleDashboardRefetchAfterReconnect()
  hasConnectedOnce = true
  isManualDisconnect = false
}

const handleSocketDisconnected = () => {
  isSocketConnected = false
  markUnexpectedNetworkDisconnect()
}

const handlePusherConnectionUnavailable = () => {
  isSocketConnected = false
  markUnexpectedNetworkDisconnect()
}

const handlePusherConnectionFailed = () => {
  isSocketConnected = false
  markUnexpectedNetworkDisconnect()
}

const handlePusherStateChange = (states) => {
  const { previous, current } = states

  // Network blips often go connected -> connecting and never emit disconnected.
  if (hasConnectedOnce && previous === "connected" && current !== "connected") {
    isSocketConnected = false
    markUnexpectedNetworkDisconnect()
    return
  }

  if (UNHEALTHY_CONNECTION_STATES.includes(current)) {
    isSocketConnected = false
    markUnexpectedNetworkDisconnect()
  }
}

const handleBrowserOffline = () => {
  if (hasConnectedOnce) {
    isSocketConnected = false
    markUnexpectedNetworkDisconnect()
  }
}

const handleBrowserOnline = () => {
  if (hadNetworkDisconnect && isSocketConnected) {
    scheduleDashboardRefetchAfterReconnect()
  }
}

const disconnectSocketIntentionally = () => {
  if (!socket) {
    return
  }

  isManualDisconnect = true
  isSocketConnected = false
  try {
    socket.disconnect()
  } finally {
    isManualDisconnect = false
  }
}

const unbindSocketConnectionStateListeners = () => {
  socketConnectionCleanup?.()
  socketConnectionCleanup = null
}

const bindSocketConnectionStateListeners = () => {
  unbindSocketConnectionStateListeners()

  if (!socket?.connector) {
    return
  }

  const provider = import.meta.env.VITE_SOCKET_PROVIDER

  if (provider === "SOCKETIO") {
    const ioSocket = socket.connector.socket
    if (!ioSocket) {
      return
    }

    if (ioSocket.connected) {
      isSocketConnected = true
      hasConnectedOnce = true
    }
    ioSocket.on("connect", handleSocketConnected)
    ioSocket.on("disconnect", handleSocketDisconnected)
    socketConnectionCleanup = () => {
      ioSocket.off("connect", handleSocketConnected)
      ioSocket.off("disconnect", handleSocketDisconnected)
    }
    return
  }

  const connection = socket.connector.pusher?.connection
  if (!connection) {
    return
  }

  if (connection.state === "connected") {
    isSocketConnected = true
    hasConnectedOnce = true
  }
  connection.bind("connected", handleSocketConnected)
  connection.bind("disconnected", handleSocketDisconnected)
  connection.bind("unavailable", handlePusherConnectionUnavailable)
  connection.bind("failed", handlePusherConnectionFailed)
  connection.bind("state_change", handlePusherStateChange)
  socketConnectionCleanup = () => {
    connection.unbind("connected", handleSocketConnected)
    connection.unbind("disconnected", handleSocketDisconnected)
    connection.unbind("unavailable", handlePusherConnectionUnavailable)
    connection.unbind("failed", handlePusherConnectionFailed)
    connection.unbind("state_change", handlePusherStateChange)
  }
}

const initDashboardSockets = () => {
  window.dashboardChannel.passesChannel.listen(
    "PassCommentCreatedEvent",
    (payload) => {
      //Update counters
      store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
      //Update passes table
      store.commit("dashboardTable/UPDATE_PASS", payload.pass)
      if (payload.pass.type == "APT" && payload.pass.comments_count == 1) {
        return
      } else {
        helpers.checkForNotificationSound(payload)
      }
    }
  )

  window.dashboardChannel.appPassChannel.listen(
    "PassCommentCreatedEvent",
    (payload) => {
      if (payload && payload.pass) {
        const activeTab = store.getters["adultAptPass/activeTab"]
        const activeRoute = store.getters["routerDependencies/activeRoute"]

        if (
          activeRoute.name === "School Dashboard" ||
          activeTab == "nextSevenDays"
        ) {
          store.commit("adultAptPass/UPDATE_APP_PASS", payload.pass)
        }
      }
      helpers.checkForNotificationSound(payload)
    }
  )

  window.dashboardChannel.appPassChannel.listen(
    "AppointmentPassCreated",
    (payload) => {
      if (payload && payload.aptPass) {
        store.commit("dashboardCounters/UPDATE_APP_COUNTERS", [payload.aptPass])
        const activeTab = store.getters["adultAptPass/activeTab"]
        const activeRoute = store.getters["routerDependencies/activeRoute"]

        if (
          activeRoute.name == "School Dashboard" ||
          activeTab === "nextSevenDays" ||
          activeTab === "prevSevenDays"
        ) {
          store.commit("adultAptPass/PUSH_APPOINTMENT_PASS", payload.aptPass)
        }
      }
    }
  )
  window.dashboardChannel.appPassChannel.listen(
    "AppointmentPassCancelled",
    (payload) => {
      if (payload && payload.appointmentPass) {
        store.commit("dashboardCounters/UPDATE_APP_COUNTERS", [
          payload.appointmentPass
        ])
        let activeTab = store.getters["adultAptPass/activeTab"]
        const activeRoute = store.getters["routerDependencies/activeRoute"]

        if (
          activeRoute.name == "School Dashboard" ||
          (helpers.isAptInSelectedTab(payload.appointmentPass) &&
            (activeTab == "nextSevenDays" ||
              (activeTab == "prevSevenDays" &&
                !payload.appointmentPass.recurrence_appointment_pass)))
        ) {
          store.commit("adultAptPass/UPDATE_APP_PASS", payload.appointmentPass)
        }
      }
    }
  )

  window.dashboardChannel.appPassChannel.listen(
    "AppointmentPassRecurrenceCanceled",
    (payload) => {
      if (payload && payload.appointmentPass) {
        store.commit("dashboardCounters/UPDATE_APP_COUNTERS", [
          payload.appointmentPass
        ])
        let activeTab = store.getters["adultAptPass/activeTab"]
        const activeRoute = store.getters["routerDependencies/activeRoute"]

        if (
          ((activeRoute.name == "Dashboard" || activeTab == "nextSevenDays") &&
            payload.deleteAction == 2) ||
          activeTab == "prevSevenDays"
        ) {
          store.commit(
            "adultAptPass/UPDATE_APP_PASSES_BY_RECURRENCE",
            payload.appointmentPass
          )
        }
      }
    }
  )

  window.dashboardChannel.appPassChannel.listen(
    "AppointmentPassUpdated",
    (payload) => {
      store.commit("dashboardCounters/UPDATE_APP_COUNTERS", [
        payload.appointmentPass
      ])
      const activePassType = store.getters["dashboardTable/getType"]
      const activeTab = store.getters["adultAptPass/activeTab"]
      if (
        (activePassType.includes("appointment") &&
          activeTab == "nextSevenDays") ||
        (!activePassType.includes("appointment") && payload?.appointmentPass)
      ) {
        store.commit("adultAptPass/UPDATE_APP_PASS", payload.appointmentPass)
      }
    }
  )
  window.dashboardChannel.appPassChannel.listen(
    "RecurrenceAppointmentPassCreated",
    (payload) => {
      const activePassType = store.getters["dashboardTable/getType"]
      const activeTab = store.getters["adultAptPass/activeTab"]
      if (
        activePassType.includes("appointment") &&
        activeTab == "prevSevenDays" &&
        payload?.recurrenceAppointmentPass
      ) {
        store.commit(
          "adultAptPass/PUSH_APPOINTMENT_PASS",
          payload.recurrenceAppointmentPass
        )
      }
    }
  )

  window.dashboardChannel.appPassChannel.listen(
    "RecurrenceAppointmentPassEdited",
    (payload) => {
      const activePassType = store.getters["dashboardTable/getType"]
      const activeTab = store.getters["adultAptPass/activeTab"]
      if (
        activePassType.includes("appointment") &&
        activeTab == "prevSevenDays" &&
        payload?.recurrenceAppointmentPass
      ) {
        store.commit(
          "adultAptPass/UPDATE_APP_PASS",
          payload.recurrenceAppointmentPass
        )
      }
    }
  )
  window.dashboardChannel.appPassChannel.listen(
    "AppointmentPassMissed",
    (payload) => {
      const activePassType = store.getters["dashboardTable/getType"]
      const activeTab = store.getters["adultAptPass/activeTab"]
      if (
        activePassType.includes("appointment") &&
        activeTab == "nextSevenDays" &&
        payload?.appointmentPass
      ) {
        store.commit("adultAptPass/UPDATE_APP_PASS", payload.appointmentPass)
      }
    }
  )
  window.dashboardChannel.appPassChannel.listen(
    "AppointmentPassesDeleted",
    (payload) => {
      const activePassType = store.getters["dashboardTable/getType"]
      const activeTab = store.getters["adultAptPass/activeTab"]
      if (
        activePassType.includes("appointment") &&
        activeTab == "nextSevenDays" &&
        payload?.appointmentIdsForDelete
      ) {
        store.commit(
          "adultAptPass/DELETE_APTS_BY_ID",
          payload.appointmentIdsForDelete
        )
        store.commit(
          "dashboardCounters/DELETE_APP_COUNTERS_BY_ID",
          payload.appointmentIdsForDelete
        )
      }
    }
  )

  window.dashboardChannel.appPassChannel.listen(
    "AppointmentPassEnded",
    (payload) => {
      const activePassType = store.getters["dashboardTable/getType"]
      const activeTab = store.getters["adultAptPass/activeTab"]
      if (
        activePassType.includes("appointment") &&
        activeTab == "nextSevenDays" &&
        payload?.appointmentPass
      ) {
        store.commit("adultAptPass/UPDATE_APP_PASS", payload.appointmentPass)
      }
    }
  )

  window.dashboardChannel.schoolChannel.listen(
    "PassMinTimeReached",
    (payload) => {
      //Update counters
      store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
      //Update passes table
      store.commit("dashboardTable/UPDATE_PASS", payload.pass)
    }
  )
  window.dashboardChannel.schoolChannel.listen(
    "AdminPassApproved",
    (payload) => {
      //Update counters
      store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
      //Update passes table
      store.commit("dashboardTable/UPDATE_PASS", payload.pass)
      helpers.checkForNotificationSound(payload)
    }
  )
  window.dashboardChannel.schoolChannel.listen("TotalPassReportEvent", () => {
    store.commit("totalPassReports/SET_IS_REPORT_NEW", true)
  })
  window.dashboardChannel.schoolChannel.listen(
    "AdminPassArrived",
    (payload) => {
      //Update counters
      store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
      //Update passes table
      store.commit("dashboardTable/UPDATE_PASS", payload.pass)
    }
  )
  window.dashboardChannel.schoolChannel.listen("AdminPassInOut", (payload) => {
    //Update counters
    store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
    //Update passes table
    store.commit("dashboardTable/UPDATE_PASS", payload.pass)
  })
  window.dashboardChannel.schoolChannel.listen(
    "AdminPassReturning",
    (payload) => {
      //Update counters
      store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
      //Update passes table
      store.commit("dashboardTable/UPDATE_PASS", payload.pass)
    }
  )
  window.dashboardChannel.schoolChannel.listen("AdminPassEnded", (payload) => {
    //Update counters
    store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
    //Update passes table
    store.commit("dashboardTable/UPDATE_PASS", payload.pass)
  })
  window.dashboardChannel.schoolChannel.listen("PassCanceled", (payload) => {
    //Update counters
    store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
    //Update passes table
    store.commit("dashboardTable/UPDATE_PASS", payload.pass)
  })
  window.dashboardChannel.schoolChannel.listen(
    "AdminPassCanceled",
    (payload) => {
      //Update counters
      store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
      //Update passes table
      store.commit("dashboardTable/UPDATE_PASS", payload.pass)
    }
  )
  window.dashboardChannel.schoolChannel.listen(
    "AdminPassExpired",
    (payload) => {
      //Update counters
      store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
      //Update passes table
      store.commit("dashboardTable/UPDATE_PASS", payload.pass)
    }
  )
  window.dashboardChannel.schoolChannel.listen(
    "AdminPassCreated",
    (payload) => {
      //Update counters
      store.commit("dashboardCounters/UPDATE_PASS", payload.pass)
      //Update passes table
      store.commit("dashboardTable/UPDATE_PASS", payload.pass)
      helpers.checkForNotificationSound(payload)
    }
  )
}
const attachSocketListeners = (state) => {
  const id = state.authentication.user.id
  const role = state.authentication.user.role.name
  const schoolId = state.authentication.user.school_id
  const isMuted = !state.authentication.user.audio_enabled
  const isStudent = role === "student"
  const isAdmin = role === "admin"

  // Hub auth matches e-hallpass-v2/routes/channels.php
  // inbox: any user (id + school)
  // everyone: any user in school
  // adult: !student
  // reports: !student
  // admin: admin only (not superadmin)
  const inboxChannel = socket.private(`user.inbox.${id}.${schoolId}`)
  const sharedChannel = socket.private(`school.${schoolId}.everyone`)
  const adultChannel = !isStudent
    ? socket.private(`school.${schoolId}.adult`)
    : null
  const reportsChannel = !isStudent
    ? socket.private(`user.reports.${id}.${schoolId}`)
    : null
  const adminChannel = isAdmin
    ? socket.private(`school.${schoolId}.admin`)
    : null

  if (isStudent) {
    window.studentChannels = {
      profileChannel: inboxChannel,
      studentPassLimits: sharedChannel
    }

    // QueuePassCreated
    inboxChannel.listen("QueueManagement\\QueuePassCreated", (payload) => {
      // eslint-disable-next-line no-console
      console.log("inboxChannel QueuePassCreated event received:", payload)
      if (payload?.queuePass) {
        store.commit("passes/REPLACE_ACTIVE_PASS", payload.queuePass)
        if (
          queueUtils.shouldPlayNotification({
            eventType: "QueuePassCreated",
            payload,
            user: state?.authentication?.user,
            role,
            userId: id,
            store
          })
        ) {
          helpers.playNotificationSound({ playQueueSound: true })
        }
      }
    })

    // QueuePassConverted
    inboxChannel.listen("QueueManagement\\QueuePassConverted", (payload) => {
      // eslint-disable-next-line no-console
      console.log("inboxChannel QueuePassConverted event received:", payload)
      if (payload?.pass) {
        store.commit("passes/REPLACE_ACTIVE_PASS", payload.pass)

        if (
          queueUtils.shouldPlayNotification({
            eventType: "QueuePassConverted",
            payload,
            user: state?.authentication?.user,
            role,
            userId: id,
            store
          })
        ) {
          helpers.playNotificationSound({ playQueueSound: true })
        }
      }
    })

    // QueuePassMoved
    inboxChannel.listen("QueueManagement\\QueuePassMoved", (payload) => {
      // eslint-disable-next-line no-console
      console.log("inboxChannel QueuePassMoved event received:", payload)
      if (payload?.queuePass) {
        store.commit("passes/SET_ACTIVE_PASS", payload.queuePass)

        if (
          queueUtils.shouldPlayNotification({
            eventType: "QueuePassMoved",
            payload,
            user: state?.authentication?.user,
            role,
            userId: id,
            store
          })
        ) {
          helpers.playNotificationSound({ playQueueSound: true })
        }
      }
    })

    // QueuePassCanceled
    inboxChannel.listen("QueueManagement\\QueuePassCanceled", (payload) => {
      // eslint-disable-next-line no-console
      console.log("inboxChannel QueuePassCanceled event received:", payload)
      if (payload?.queuePass) {
        store.commit("passes/SET_ACTIVE_PASS", null)
        if (
          queueUtils.shouldPlayNotification({
            eventType: "QueuePassCanceled",
            payload,
            user: state?.authentication?.user,
            role,
            userId: id,
            store
          })
        ) {
          helpers.playNotificationSound({ playQueueSound: true })
        }
        window?.location?.replace("/passes/create")
      }
    })

    sharedChannel.listen("Tardy\\StudentLatePassCreationToggledEvent", () => {
      store.dispatch("schools/getActiveModules", {
        clearCacheEntry: true
      })
    })

    inboxChannel.listen("AppointmentPassRemind", (payload) => {
      if (state.authentication.user.audio_enabled) {
        helpers.playNotificationSound()
      }
      store.commit(
        "sockets/SET_APPOINTMENT_PASS_REMIND",
        payload.appointmentPass
      )
      store.dispatch("sockets/getNotifications")
    })

    inboxChannel.listen("AppointmentPassRun", (payload) => {
      if (state.authentication.user.audio_enabled) {
        helpers.playNotificationSound()
      }
      store.commit("sockets/SET_APPOINTMENT_PASS_RUN", payload.appointmentPass)
      store.commit("studentAptPass/UPDATE_APP_PASS", payload.appointmentPass)
      store.commit("sockets/REMOVE_NOTIFICATION", "appointmentPassRemind")
      store.dispatch("sockets/getNotifications")
    })

    inboxChannel.listen("AppointmentPassCancelled", (payload) => {
      if (state.authentication.user.audio_enabled) {
        helpers.playNotificationSound()
      }
      store.dispatch("sockets/getNotifications")
      let activeTab = store.getters["studentAptPass/activeTab"]
      if (
        helpers.isAptInSelectedTabStudent(payload.appointmentPass) &&
        (activeTab == "nextSevenDays" ||
          (activeTab == "prevSevenDays" &&
            !payload.appointmentPass.recurrence_appointment_pass)) &&
        payload &&
        payload.appointmentPass
      ) {
        store.commit("studentAptPass/UPDATE_APP_PASS", payload.appointmentPass)
        store.commit("studentAptPass/UPDATE_TODAYS_APP", [
          payload.appointmentPass
        ])
      }
    })
    inboxChannel.listen("AppointmentPassRecurrenceCanceled", (payload) => {
      if (state.authentication.user.audio_enabled) {
        helpers.playNotificationSound()
      }
      store.dispatch("sockets/getNotifications")
      let activeTab = store.getters["studentAptPass/activeTab"]
      if (
        payload?.appointmentPass &&
        ((activeTab == "nextSevenDays" && payload?.deleteAction == 2) ||
          (activeTab == "prevSevenDays" &&
            !payload?.appointmentPass?.recurrence_appointment_pass))
      ) {
        store.commit(
          "studentAptPass/UPDATE_APP_PASSES_BY_RECURRENCE",
          payload.appointmentPass
        )
        store.commit("studentAptPass/UPDATE_TODAYS_APP", [
          payload.appointmentPass
        ])
      }
    })
    inboxChannel.listen("AppointmentPassTimeChanged", (payload) => {
      if (state.authentication.user.audio_enabled) {
        helpers.playNotificationSound()
      }
      store.dispatch("sockets/getNotifications")
      if (
        payload &&
        payload.instance !== "App\\Models\\RecurrenceAppointmentPass"
      ) {
        store.commit("studentAptPass/UPDATE_APP_PASS", payload.appointmentPass)
        store.commit("studentAptPass/UPDATE_TODAYS_APP", [
          payload.appointmentPass
        ])
      }
    })
    inboxChannel.listen("AppointmentPassConfirmed", (payload) => {
      if (state.authentication.user.audio_enabled) {
        helpers.playNotificationSound()
      }
      store.dispatch("sockets/getNotifications")
      let activeTab = store.getters["studentAptPass/activeTab"]
      if (
        helpers.isAptInSelectedTabStudent(payload.appointmentPass) &&
        (activeTab == "nextSevenDays" ||
          (activeTab == "prevSevenDays" &&
            !payload.appointmentPass.recurrence_appointment_pass)) &&
        payload &&
        payload.appointmentPass
      ) {
        store.commit("studentAptPass/UPDATE_APP_PASS", payload.appointmentPass)
        store.commit("studentAptPass/UPDATE_TODAYS_APP", [
          payload.appointmentPass
        ])
      }
    })
    inboxChannel.listen("RecurrenceAppointmentPassCreated", (payload) => {
      const activeTab = store.getters["studentAptPass/activeTab"]
      if (
        activeTab == "prevSevenDays" &&
        payload &&
        payload.recurrenceAppointmentPass
      ) {
        store.commit(
          "studentAptPass/UPDATE_APP_PASS",
          payload.recurrenceAppointmentPass
        )
      }
    })

    inboxChannel.listen("RecurrenceAppointmentPassEdited", (payload) => {
      const activeTab = store.getters["studentAptPass/activeTab"]
      if (
        activeTab == "prevSevenDays" &&
        payload &&
        payload.recurrenceAppointmentPass
      ) {
        store.commit(
          "studentAptPass/UPDATE_APP_PASS",
          payload.recurrenceAppointmentPass
        )
      }
    })
    inboxChannel.listen("AppointmentPassCreated", (payload) => {
      store.commit("studentAptPass/UPDATE_TODAYS_APP", [payload.aptPass])
      const activeTab = store.getters["studentAptPass/activeTab"]
      if (
        helpers.isAptInSelectedTabStudent(payload.aptPass) &&
        (activeTab == "nextSevenDays" ||
          (activeTab == "prevSevenDays" &&
            !payload.aptPass.recurrence_appointment_pass)) &&
        payload &&
        payload.aptPass
      ) {
        store.commit("studentAptPass/UPDATE_APP_PASS", payload.aptPass)
      }
    })
    inboxChannel.listen("AppointmentPassUpdated", (payload) => {
      const activeTab = store.getters["studentAptPass/activeTab"]
      if (activeTab == "nextSevenDays" && payload && payload.appointmentPass) {
        store.commit("studentAptPass/UPDATE_APP_PASS", payload.appointmentPass)
        store.commit("studentAptPass/UPDATE_TODAYS_APP", [
          payload.appointmentPass
        ])
      }
    })
    inboxChannel.listen("AppointmentPassesDeleted", (payload) => {
      const activeTab = store.getters["studentAptPass/activeTab"]
      if (
        activeTab == "nextSevenDays" &&
        payload &&
        payload.appointmentIdsForDelete
      ) {
        store.commit(
          "studentAptPass/DELETE_APTS_BY_ID",
          payload.appointmentIdsForDelete
        )
        store.commit(
          "studentAptPass/DELETE_TODAYS_APP_BY_ID",
          payload.appointmentIdsForDelete
        )
      }
    })
    inboxChannel.listen("PassCommentCreatedEvent", (payload) => {
      if (payload && payload.pass) {
        store.commit("studentAptPass/UPDATE_APP_PASS", payload.pass)
      }
    })

    inboxChannel.listen("PassApproved", (payload) => {
      if (!isMuted && payload.notifyStudent) {
        helpers.playNotificationSound()
      }
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })

    inboxChannel.listen("PassMinTimeReachedStudent", (payload) => {
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })

    inboxChannel.listen("PassCanceled", (payload) => {
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })
    inboxChannel.listen("PassCreated", (payload) => {
      if (payload.notifyStudent && !isMuted) {
        helpers.playNotificationSound()
      }
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      const params = {
        from_id: payload.pass.from_id,
        from_type: payload.pass.from_type,
        to_id: payload.pass.to_id,
        to_type: payload.pass.to_type
      }
      store.dispatch("passes/getActiveFlags", params)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })

    inboxChannel.listen("PassExpired", (payload) => {
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })

    inboxChannel.listen("PassArrived", (payload) => {
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })
    inboxChannel.listen("PassEnded", (payload) => {
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })
    inboxChannel.listen("PassInOut", (payload) => {
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })
    inboxChannel.listen("PassReturning", (payload) => {
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })
    inboxChannel.listen("PassCommentCreatedEvent", (payload) => {
      if (state.authentication.user.audio_enabled) {
        helpers.playNotificationSound()
      }
      store.commit("passes/SET_ACTIVE_PASS", payload.pass)
      if (window.vueRouter.currentRoute.path !== "/passes/activepass") {
        window.vueRouter.push("/passes/activepass")
      }
    })

    inboxChannel.listen("PinStatusEvent", (payload) => {
      const pinAttempts = payload.pinAttempts
      if (pinAttempts === 0) {
        store.commit("authentication/RESET_PIN_ATTEMPTS_COUNT")
      } else if (pinAttempts === 3) {
        store.dispatch("authentication/logOut").then(() => {
          window.location.reload()
        })
      } else if (pinAttempts === 5) {
        store
          .dispatch("authentication/getUserDetails", {
            clearCacheEntry: true
          })
          .then(() => {
            store.commit("authentication/SET_USER_IS_LOCKED", true)
            window.vueRouter.push("/locked")
          })
      }
    })

    inboxChannel.listen("UserUnlockedEvent", () => {
      store
        .dispatch("authentication/getUserDetails", {
          clearCacheEntry: true
        })
        .then(() => {
          store.commit("authentication/RESET_PIN_ATTEMPTS_COUNT")
          window.vueRouter.push("/passes/activepass")
          store.commit("authentication/SET_USER_IS_LOCKED", false)
        })
    })

    sharedChannel.listen("PassBlockRunnedEvent", (payload) => {
      store.commit("passBlocks/SET_ACTIVE_PASS_BLOCK", payload.passBlock)
    })

    sharedChannel.listen("FavoriteUnavailableEvent", (payload) => {
      if (
        payload.forFavorite &&
        payload.forFavorite.type === "App\\Models\\User"
      ) {
        store.commit(
          "favorites/UPDATE_FAVORITE_UNAVAILABILITY",
          payload.forFavorite
        )
      }
    })

    attachPbisPointsSocketListeners(inboxChannel, store, {
      onMutation: () => {
        if (state.authentication.user?.audio_enabled) {
          helpers.playNotificationSound()
        }
        store.dispatch("sockets/getNotifications")
      }
    })
  } else {
    attachPbisPointsSocketListeners(adultChannel, store)

    window.dashboardChannel = {
      appPassChannel: adultChannel,
      schoolChannel: adultChannel,
      passesChannel: adultChannel,
      dashboardChannel: reportsChannel,
      profileChannel: inboxChannel
    }

    // AdminQueuePassCreated
    adultChannel.listen("QueueManagement\\AdminQueuePassCreated", (payload) => {
      // eslint-disable-next-line no-console
      console.log(
        "schoolQueuePassChannel AdminQueuePassCreated event received:",
        payload
      )
      if (
        payload?.queuePass &&
        queueUtils.shouldReceiveUpdate({
          payload,
          userId: id,
          store,
          skipNotifyCheck: false,
          routeCheck: {
            requiredRoute: "/dashboard",
            currentRoute: window?.location?.pathname
          }
        })
      ) {
        store.commit("dashboardTable/UPDATE_QUEUE_PASSES", [payload.queuePass])
        if (
          queueUtils.shouldPlayNotification({
            eventType: "AdminQueuePassCreated",
            payload,
            user: state?.authentication?.user,
            role,
            userId: id,
            store
          })
        ) {
          helpers.playNotificationSound({ playQueueSound: true })
        }
      }
    })

    // AdminQueuePassesMoved
    adultChannel.listen("QueueManagement\\AdminQueuePassesMoved", (payload) => {
      // eslint-disable-next-line no-console
      console.log(
        "schoolQueuePassChannel AdminQueuePassesMoved event received:",
        payload
      )
      // AdminQueuePassesMoved event received
      if (
        payload?.queuePass &&
        queueUtils.shouldReceiveUpdate({
          payload,
          userId: id,
          store,
          skipNotifyCheck: true,
          routeCheck: {
            requiredRoute: "/dashboard",
            currentRoute: window?.location?.pathname
          }
        })
      ) {
        store.commit("dashboardTable/SET_UPDATE_QUEUE_PASSES_TABLE", true)
      } else {
        store.commit("dashboardTable/SET_UPDATE_QUEUE_PASSES_TABLE", false)
      }
    })

    // AdminQueuePassConverted
    adultChannel.listen(
      "QueueManagement\\AdminQueuePassConverted",
      (payload) => {
        // eslint-disable-next-line no-console
        console.log(
          "schoolQueuePassChannel AdminQueuePassConverted event received:",
          payload
        )
        if (
          payload?.queuePass &&
          queueUtils.shouldReceiveUpdate({
            payload,
            userId: id,
            store,
            skipNotifyCheck: true,
            routeCheck: {
              requiredRoute: "/dashboard",
              currentRoute: window?.location?.pathname
            }
          })
        ) {
          const currentQueuePasses = Array.from(
            store?.state?.dashboardTable?.queuePasses?.values()
          )
          const filteredPasses = currentQueuePasses.filter(
            (pass) => Number(pass?.id) !== Number(payload?.queuePass?.id)
          )
          store.commit("dashboardTable/SET_QUEUE_PASSES", filteredPasses)
          if (
            queueUtils.shouldPlayNotification({
              eventType: "AdminQueuePassConverted",
              payload,
              user: state?.authentication?.user,
              role,
              userId: id,
              store
            })
          ) {
            helpers.playNotificationSound({ playQueueSound: true })
          }
        }
      }
    )

    initDashboardSockets()

    if (adminChannel) {
      adminChannel.listen("ProvideUsersByApiOnlyToggledEvent", () => {
        store.dispatch("schools/getSchools", {
          clearCacheEntry: true
        })
        window.lf.dropInstance()
        localforage.clear()
      })

      adminChannel.listen("SftpImportLogUpdateEvent", (data) => {
        if (data.import_log) {
          switch (data.import_log.module) {
            case "rooms":
              store.commit(
                "rooms/UPDATE_ROOM_FILE_FROM_LISTENER",
                data.import_log
              )
              break
            case "limit_student_pass":
              store.commit(
                "passLimits/UPDATE_PASS_LIMIT_FILE_FROM_LISTENER",
                data.import_log
              )
              break
            case "kiosk_passcode":
              store.commit(
                "users/UPDATE_KIOSK_PC_FILE_FROM_LISTENER",
                data.import_log
              )
              break
            default:
              break
          }
        }
      })

      adminChannel.listen("VmsImportLogUpdateEvent", (data) => {
        if (data.import_log) {
          store.commit(
            "visitorSettings/UPDATE_STUDENT_GUARDIAN_FROM_LISTENER",
            data.import_log
          )
        }
      })

      adminChannel.listen("Tardy\\MaxLadderStepsReached", (payload) => {
        store.commit(
          "exceededIncidents/SET_EXCEEDED_INCIDENTS",
          payload.maxStepsReached
        )
      })
    }

    inboxChannel.listen("UnavailableUpdate", () => {
      store.dispatch("authentication/getUserInitialUnavailability")
    })

    adultChannel.listen("UnavailableExpired", () => {
      store.dispatch("authentication/getUserInitialUnavailability")
    })

    adultChannel.listen("ActivePassLimitChanged", (payload) => {
      if (payload) {
        store.commit("activePass/SET_ACTIVE_PASS_LIMITS_LISENER", payload)
      }
    })

    adultChannel.listen("LimitLocationEvent", (payload) => {
      if (payload) {
        store.commit("dashboardTable/UPDATE_AUTO_PASS_LIMIT_LISTENER", payload)
      }
    })

    adultChannel.listen("TransparencyToggledEvent", () => {
      store.dispatch("authentication/getUserDetails")
    })

    adultChannel.listen("PinChangedEvent", (payload) => {
      if (payload) {
        store.commit("rooms/SET_USER_ASSIGN_ROOMS_CHANGE_PIN", payload)
        store.commit("rooms/SET_USER_ASSIGN_ROOMS_LISENER", payload)
      }
    })

    if (isAdmin || role === "superadmin") {
      reportsChannel.listen(
        "NotificationToDownloadSummaryReportRequestEvent",
        () => {
          if (state.authentication.user.audio_enabled) {
            helpers.playNotificationSound()
          }
          store.dispatch("sockets/getNotifications")
        }
      )

      reportsChannel.listen("ReportCreatedEvent", () => {
        store.commit("summaryReports/SET_IS_REPORT_NEW", true)
      })
    }

    reportsChannel.listen("KiosksUserFileProcessedEvent", (payload) => {
      store.commit("kiosks/SET_KIOSK_PASSCODES_NAME", payload)
    })

    reportsChannel.listen("Files\\FileCompletedEvent", (payload) => {
      if (payload.file) {
        store.commit(
          "users/SET_STUDENT_CARDS_LINK",
          payload.file.download_url || null
        )
      }
    })

    reportsChannel.listen("PassHistoryReportEvent", async (payload) => {
      const toast = useToast()
      try {
        const response = await store.dispatch(
          "passes/passHistoryCsvDownload",
          payload.filename
        )
        const fileContents = response?.data?.fileContents
        const fileName = response?.data?.fileName
        download.CSVExport(fileContents, fileName, "")
        await store.dispatch("passes/deleteCsvFile", fileName)
      } catch (error) {
        const errMessage = error?.response?.data?.message
        toast.error(errMessage)
      }
    })

    reportsChannel.listen("Tardy\\TardyReportReadyEvent", (payload) => {
      if (!payload.isDownloadCsv) {
        store.commit("reports/SET_REPORT_READY_EVENT_PAYLOAD", payload)
      }
    })

    reportsChannel.listen("Tardy\\TardyDetailedReportReadyEvent", (payload) => {
      store.commit("reports/SET_DETAILED_REPORT_READY_EVENT_PAYLOAD", payload)
    })

    reportsChannel.listen(
      "Tardy\\TardyAttendanceReportEvent",
      async (payload) => {
        const toast = useToast()
        try {
          const response = await store.dispatch(
            "reports/tardyReportCsvDownload",
            payload.filename
          )
          const fileContents = response?.data?.fileContents
          const fileName = response?.data?.fileName
          download.CSVExport(fileContents, fileName, "")
          await store.dispatch("reports/deleteCsvFile", fileName)
        } catch (error) {
          const errMessage = error?.response?.data?.message
          toast.error(errMessage)
        }
      }
    )

    reportsChannel.listen("MeetUpReportGeneratedEvent", (payload) => {
      store.commit("contactTracing/MEETUP_REPORT_GENERATED", payload)
    })

    inboxChannel.listen("AppointmentPassAdultBell", () => {
      if (state.authentication.user.audio_enabled) {
        helpers.playNotificationSound()
      }
      store.dispatch("sockets/getNotifications")
    })
  }

  // All roles: inbox + everyone
  inboxChannel.listen("LogoutUserEvent", () => {
    disconnectSocketIntentionally()
    store.dispatch("authentication/unauthorizeArchivedUser")
  })

  inboxChannel.listen("Tardy\\LatePassCreationToggled", () => {
    store.dispatch("schools/getActiveModules", {
      clearCacheEntry: true
    })
    store.dispatch("authentication/getUserDetails", {
      clearCacheEntry: true
    })
  })

  inboxChannel.listen("ProfileUpdatedEvent", (payload) => {
    store.commit("authentication/SET_USER", payload.user)
  })
  inboxChannel.listen("Vms\\VmsCheckinReportGenerated", (payload) => {
    store.commit("visitorManage/SET_CHECKIN_REPORT_FILE", payload.file)
  })
  sharedChannel.listen("PassBlockCreated", () => {
    store.dispatch("dashboardTable/getDashboard")
  })
  sharedChannel.listen("PassBlockRemoved", () => {
    store.dispatch("dashboardTable/getDashboard")
  })
  sharedChannel.listen("ModuleChanged", () => {
    store.dispatch("schools/getActiveModules", {
      clearCacheEntry: true
    })
  })
  sharedChannel.listen("Tardy\\FeatureTierChanged", () => {
    store
      .dispatch("schools/getSchools", {
        clearCacheEntry: true
      })
      .then(() => {
        store.dispatch("schools/getActiveModules", {
          clearCacheEntry: true
        })
      })
  })
  sharedChannel.listen("LogoutEvent", () => {
    disconnectSocketIntentionally()
    store.dispatch("authentication/logOut")
  })
  sharedChannel.listen("ClearFrontEndCacheEvent", () => {
    window.lf.dropInstance()
    localforage.clear()
  })
  sharedChannel.listen("AssignedLocationsUpdatedEvent", (payload) => {
    store.commit(
      "dashboardTable/SET_USERS_ASSIGNED_ROOMS",
      payload.userIdsAssigned
    )
    const currentRole = store.getters["authentication/getUserRole"]
    if (currentRole == "student") {
      store.dispatch("passes/getStudentActivePass")
    }
  })

  sharedChannel.listen("UserProfilePreferencesEvent", () => {
    store.dispatch("favorites/getFavorites", { clearCacheEntry: true })
  })

  Object.values(socket.connector.channels).forEach((channel) => {
    if (
      import.meta.env.VITE_SOCKET_PROVIDER == "SOKETI" ||
      import.meta.env.VITE_SOCKET_PROVIDER == "REVERB"
    ) {
      channel.on("pusher:subscription_error", (error) => {
        if (error.status == 401) {
          destroySocketReconnectConditions()
          store.dispatch("authentication/logOut")
        }
      })
    }
    if (import.meta.env.VITE_SOCKET_PROVIDER == "SOCKETIO") {
      channel.on("subscription_error", (error) => {
        if (error == 401) {
          destroySocketReconnectConditions()
          store.dispatch("authentication/logOut")
        }
      })
    }
  })
}
const reconnectSocket = () => {
  hadNetworkDisconnect = false
  disconnectSocketIntentionally()
  socket.connect()
  bindSocketConnectionStateListeners()
  attachSocketListeners(store.state)
}
const handlePageRefocusWSConnection = () => {
  if (store?.state?.authentication?.user?.id) {
    if (!document.hidden) {
      reconnectSocket()
    }
  }
}
const initSocketReconnectConditions = () => {
  // reconnect when tab is re-focused & every 2 hours

  window.addEventListener("visibilitychange", handlePageRefocusWSConnection)
  window.addEventListener("offline", handleBrowserOffline)
  window.addEventListener("online", handleBrowserOnline)
  wsReconnectInterval = setInterval(
    () => {
      if (store?.state?.authentication?.user?.id) {
        reconnectSocket()
      }
    },
    1000 * 60 * 120
  )
}
const destroySocketReconnectConditions = () => {
  disconnectSocketIntentionally()
  unbindSocketConnectionStateListeners()
  clearTimeout(dashboardRefetchDebounceTimer)
  clearTimeout(dashboardRefetchCooldownTimer)
  hadNetworkDisconnect = false
  hasConnectedOnce = false
  refetchInFlight = false
  lastDashboardRefetchAt = 0
  isSocketConnected = false
  isManualDisconnect = false
  window.removeEventListener("visibilitychange", handlePageRefocusWSConnection)
  window.removeEventListener("offline", handleBrowserOffline)
  window.removeEventListener("online", handleBrowserOnline)
  if (wsReconnectInterval) {
    clearInterval(wsReconnectInterval)
  }
}
const initWebSocketInstance = () => {
  if (import.meta.env.VITE_SOCKET_PROVIDER == "SOKETI") {
    window.Pusher = require("pusher-js")
    socket = new Echo({
      broadcaster: "pusher",
      key: import.meta.env.VITE_PUSHER_APP_KEY,
      wsHost: import.meta.env.VITE_PUSHER_WS_HOST,
      wsPort: import.meta.env.VITE_PUSHER_WS_PORT,
      cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
      forceTLS: false,
      encrypted: true,
      disableStats: true,
      enabledTransports: ["ws", "wss"],
      authEndpoint: `${import.meta.env.VITE_AUTH_URL}/api/v5/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: "Bearer " + tokenService.getToken()
        }
      }
    })
  } else if (import.meta.env.VITE_SOCKET_PROVIDER == "REVERB") {
    const reverbScheme = import.meta.env.VITE_REVERB_SCHEME
    const reverbHost = import.meta.env.VITE_REVERB_HOST

    socket = new Echo({
      broadcaster: "reverb",
      key: import.meta.env.VITE_REVERB_APP_KEY,
      wsHost: reverbHost,
      wsPort: import.meta.env.VITE_REVERB_PORT,
      wssPort: import.meta.env.VITE_REVERB_PORT,
      forceTLS: reverbScheme == "https",
      enabledTransports: ["ws", "wss"],
      authEndpoint: `${import.meta.env.VITE_AUTH_URL}/api/v5/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: "Bearer " + tokenService.getToken()
        }
      }
    })
  } else {
    window.io = require("socket.io-client")
    socket = new Echo({
      broadcaster: "socket.io",
      host: import.meta.env.VITE_WS_HOST,
      authEndpoint: `${import.meta.env.VITE_AUTH_URL}/api/v5/broadcasting/auth`,
      encrypted: import.meta.env.VITE_WS_ENCRYPTED === "true",
      auth: {
        headers: {
          Authorization: "Bearer " + tokenService.getToken()
        }
      },
      transports: ["websocket"]
    })
  }
}
export const WebSocketPlugin = () => {
  return (store) => {
    // called when the store is initialized
    let isSubscribed = false

    store.subscribe((mutation, state) => {
      if (mutation.type === "kiosks/SET_ACTIVE_KIOSK" && socket) {
        destroySocketReconnectConditions()
      }
      if (mutation.type == "authentication/SET_USER" && !isSubscribed) {
        if (state?.authentication) {
          if (state?.authentication?.user?.id) {
            initWebSocketInstance()
            bindSocketConnectionStateListeners()
            attachSocketListeners(state)
            initSocketReconnectConditions()
            isSubscribed = true
          } else {
            disconnectSocketIntentionally()
          }
        }
      }
    })
  }
}
